import React from "react";
import { styles } from "./BidHistoryReportStyles";
import { Page, Text, View, Document, Link, Image } from "@react-pdf/renderer";
import { UserState } from "../../redux/user/userReducer";
import {
  Auction,
  AuctionBidStatus,
  BidMapAddress,
  BidMapResponse,
  IAuctionBid,
} from "../../models/Auction";
import { commaFormat, formatDateTime } from "../../utils/Helper";

interface IBidHistoryReport {
  user: UserState;
  currentDate: string;
  auction: Auction;
  bids: BidMapResponse;
}

const BidHistoryReport: React.FC<IBidHistoryReport> = ({
  user,
  currentDate,
  auction,
  bids,
}) => {
  const fileName = (
    auction.title +
    "___" +
    user.company_details?.name +
    "_ Bid_History.pdf"
  ).replaceAll(" ", "_");

  return (
    <Document title={fileName}>
      <Page size="A4" style={styles.page}>
        <View fixed style={styles.borderOut}>
          <View style={styles.borderIn}></View>
        </View>
        <View>
          <View style={styles.pageHeader}>
            <View style={styles.pageHeaderLeftField}>
              <Text style={styles.pageHeaderKey}>Auction ID</Text>
              <Text style={styles.pageHeaderValue}>#{auction.auction_id}</Text>
            </View>
            <Text style={styles.pageHeaderTitle}>
              Vendor Auction Bid History
            </Text>
            <View style={styles.pageHeaderRightField}>
              <Text style={styles.pageHeaderKey}>Date</Text>
              <Text style={styles.pageHeaderValue}>{currentDate}</Text>
            </View>
          </View>
          <View style={styles.clientInfoContainer}>
            <View style={styles.leftContainer}>
              <Text style={styles.auctionType}>Reverse Auction</Text>
              <Text style={styles.auctionTitle}>{auction.title}</Text>
              <Text style={styles.auctionTime}>
                {formatDateTime(auction.start_time).replace(/-/g, "/")} -{" "}
                {formatDateTime(auction.end_time).replace(/-/g, "/")}
              </Text>
            </View>
            <View style={styles.rightContainer}>
              <Image
                style={styles.clientLogo}
                src={auction.client.avatar}
              ></Image>
              <Text style={styles.auctionTitle}>{auction.client.name}</Text>
            </View>
          </View>
          <Text style={styles.auctionDescription}>
            {auction.description.replaceAll(/<[^>]*>/g, " ")}
          </Text>
          <View style={styles.auctionBidsContainer}>
            {Object.values(bids).map((address: BidMapAddress, index) => {
              return (
                <View
                  style={styles.auctionBidAddressContainer}
                  key={`address-${index}`}
                >
                  <View style={styles.addressDetailContainer}>
                    <Text style={styles.addressState}>{address.state}</Text>
                    <Text style={styles.addressLine}>{address.name}</Text>
                  </View>
                  {address.skus.map((sku, sindex) => {
                    return (
                      <View key={`skus-${sku.id}`}>
                        <View style={styles.skuNameContainer}>
                          <View style={styles.skuLeft}>
                            <Text>
                              {"0".repeat(3 - (sindex + 1).toString().length)}
                              {sindex + 1}
                            </Text>
                            <Text>{sku.name}</Text>
                          </View>
                          <View style={styles.skuRight}>
                            <View style={styles.skuFieldContainer}>
                              <Text style={styles.skuFieldKey}>Quantity:</Text>
                              <Text style={styles.skuFieldValue}>
                                {commaFormat(sku.quantity.toString())}{" "}
                                {sku.unit}
                              </Text>
                            </View>
                            <View style={styles.skuFieldContainer}>
                              <Text style={styles.skuFieldKey}>
                                Start Price:
                              </Text>
                              <Text style={styles.skuFieldValue}>
                                INR {commaFormat(sku.start_price.toString())}
                              </Text>
                            </View>
                            <View style={styles.skuFieldContainer}>
                              <Text style={styles.skuFieldKey}>
                                Min Decreement:
                              </Text>
                              <Text style={styles.skuFieldValue}>
                                INR {commaFormat(sku.decreement.toString())}
                              </Text>
                            </View>
                          </View>
                        </View>
                        <View style={styles.bidsContainer}>
                          <View style={styles.bidsHeaderContainer}>
                            <Text style={styles.unitRateKey}>Unit Rate</Text>
                            <Text style={styles.totalRateKey}>Total Rate</Text>
                            <Text style={styles.bidTimeKey}>Bid Time</Text>
                            <Text style={styles.totalRateKey}>Status</Text>
                            <Text style={styles.rankKey}>Rank</Text>
                            <Text style={styles.remarkKey}>Remark</Text>
                          </View>
                          {sku.bids.map((bid, bindex) => (
                            <View
                              style={{
                                ...styles.bidsRowContainer,
                                borderBottom:
                                  index + 1 === Object.keys(bids).length &&
                                  sindex + 1 === address.skus.length &&
                                  bindex + 1 === sku.bids.length
                                    ? "none"
                                    : "1px solid #E5E7EB",
                                borderBottomLeftRadius:
                                  index + 1 === Object.keys(bids).length &&
                                  sindex + 1 === address.skus.length &&
                                  bindex + 1 === sku.bids.length
                                    ? "6px"
                                    : "0px",
                                borderBottomRightRadius:
                                  index + 1 === Object.keys(bids).length &&
                                  sindex + 1 === address.skus.length &&
                                  bindex + 1 === sku.bids.length
                                    ? "6px"
                                    : "0px",
                              }}
                              key={`bids-${bindex}`}
                            >
                              <Text style={styles.unitRateValue}>
                                INR {commaFormat(bid.price.toString())}
                              </Text>
                              <Text style={styles.totalRateValue}>
                                INR{" "}
                                {commaFormat(
                                  (
                                    Math.round(sku.quantity * bid.price * 100) /
                                    100
                                  ).toString()
                                )}
                              </Text>
                              <Text style={styles.bidTimeValue}>
                                {formatDateTime(bid.created_at).replace(
                                  /-/g,
                                  "/"
                                )}
                              </Text>
                              <Text
                                style={{
                                  ...styles.totalRateValue,
                                  color: AuctionBidStatus[bid.status].color,
                                  backgroundColor:
                                    AuctionBidStatus[bid.status].background,
                                  textAlign: "center",
                                }}
                              >
                                {bid.status}
                              </Text>
                              <Text style={styles.rankValue}>
                                {bid.bid_rank}
                              </Text>
                              <Text style={styles.remarkValue}>
                                {bid.remark}
                              </Text>
                            </View>
                          ))}
                        </View>
                      </View>
                    );
                  })}
                </View>
              );
            })}
          </View>
        </View>
        <View style={styles.pageFooter}>
          <View style={styles.footerLine}></View>
          <Text style={styles.pageFooterName}>
            Confidentiality Note: This document is confidential and intended for
            the recipient&lsquo;s use only. Unauthorized sharing or distribution
            is prohibited.
          </Text>
          <Text style={styles.pageFooterName}>
            Disclaimer: This report is generated from auction data as recorded
            in the system. While every effort is made to ensure accuracy, Xetgo
            Private Limited is not liable for errors arising from technical
            issues, vendor inputs, or delays. All data is subject to
            verification.
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default BidHistoryReport;
